import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import api, { BACKEND_URL } from '@/lib/api';
import { toast } from 'sonner';
import { Camera, Save, User, Mail, Building2, Phone, Shield } from 'lucide-react';

const roleLabels = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  merchant: 'Merchant',
  buyer: 'Buyer',
  internal: 'Internal',
};

export default function ProfilePage() {
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', contact: '' });
  const [photoUploading, setPhotoUploading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get('/profile');
      setProfile(res.data);
      setForm({ name: res.data.name || '', company: res.data.company || '', contact: res.data.contact || '' });
    } catch (e) { toast.error('Failed to load profile'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const res = await api.put('/profile', form);
      setProfile(res.data);
      toast.success('Profile updated');
    } catch (e) { toast.error('Failed to update profile'); }
    setSaving(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProfile(p => ({ ...p, ...res.data }));
      toast.success('Photo updated');
    } catch (e) { toast.error('Failed to upload photo'); }
    setPhotoUploading(false);
  };

  const getPhotoSrc = () => {
    if (!profile) return null;
    if (profile.photo_url?.startsWith('http')) return profile.photo_url;
    if (profile.photo_path || profile.photo_url) {
      return `${BACKEND_URL}/api/profile/photo/serve?user_id=${profile.id}&auth=${localStorage.getItem('token')}`;
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="profile-page" className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-[#0A0A0A] tracking-tight">Profile</h1>
          <p className="text-sm text-[#71717A] mt-1">Manage your account details</p>
        </div>

        {/* Photo + Role Card */}
        <div className="bg-white rounded-md border border-black/[0.06] p-8">
          <div className="flex items-center gap-6">
            <div className="relative group">
              <div className="w-24 h-24 rounded-full bg-[#F5F5F5] flex items-center justify-center overflow-hidden border-2 border-black/[0.06]">
                {getPhotoSrc() ? (
                  <img src={getPhotoSrc()} alt={profile?.name} className="w-full h-full object-cover" data-testid="profile-photo" />
                ) : (
                  <User className="w-10 h-10 text-[#D4D4D8]" />
                )}
              </div>
              <label className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                {photoUploading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-5 h-5 text-white" />
                )}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={photoUploading} data-testid="photo-upload-input" />
              </label>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#0A0A0A]">{profile?.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-3 h-3 text-[#A1A1AA]" />
                <span className="text-sm text-[#71717A]">{profile?.email}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Shield className="w-3 h-3 text-[#A1A1AA]" />
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-[#F0F0F0] text-[#52525B]">
                  {roleLabels[profile?.role] || profile?.role}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="bg-white rounded-md border border-black/[0.06] p-8 space-y-5">
          <h3 className="text-sm font-semibold text-[#0A0A0A] uppercase tracking-wider">Details</h3>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="h-10 rounded-md" data-testid="profile-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Company</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input value={form.company} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} className="h-10 rounded-md pl-10" data-testid="profile-company-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Contact</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input value={form.contact} onChange={(e) => setForm(p => ({ ...p, contact: e.target.value }))} className="h-10 rounded-md pl-10" placeholder="+91 98765 43210" data-testid="profile-contact-input" />
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-10 btn-premium" data-testid="save-profile-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
